<template>
  <div class="tutorial-client">
    <div class="space-y-6">
      <!-- Client Connection Options -->
      <div class="grid md:grid-cols-2 gap-6">
        <!-- Camera Scanner -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <IconifyIcon icon="heroicons:camera-20-solid" class="w-5 h-5 text-blue-600" />
            Scan with Camera
          </h4>

          <!-- Simulated Camera View -->
          <div class="space-y-4">
            <div class="relative bg-black rounded-lg overflow-hidden" style="aspect-ratio: 4/3;">
              <!-- Camera Placeholder -->
              <div class="absolute inset-0 flex items-center justify-center">
                <div v-if="!cameraActive" class="text-center">
                  <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-16 h-16 text-gray-500 mx-auto mb-2" />
                  <p class="text-gray-400">Camera preview</p>
                </div>
                <div v-else class="w-full h-full">
                  <!-- Simulated camera feed with QR code detection animation -->
                  <div class="w-full h-full relative bg-gradient-to-br from-gray-800 to-gray-900">
                    <!-- Scanning animation -->
                    <div class="absolute inset-4 border-2 border-green-400 rounded-lg">
                      <div class="absolute top-0 left-0 w-full h-1 bg-green-400 animate-pulse"></div>
                      <div class="absolute top-0 left-0 w-1 h-full bg-green-400 animate-pulse"></div>
                      <div class="absolute bottom-0 left-0 w-full h-1 bg-green-400 animate-pulse"></div>
                      <div class="absolute top-0 right-0 w-1 h-full bg-green-400 animate-pulse"></div>
                    </div>
                    <!-- QR Code overlay -->
                    <div class="absolute inset-0 flex items-center justify-center">
                      <div class="w-24 h-24 bg-white p-1 rounded">
                        <div class="w-full h-full bg-black grid grid-cols-5 gap-0.5">
                          <template v-for="_ in 25" :key="_">
                            <div
                              class="rounded-sm"
                              :class="Math.random() > 0.3 ? 'bg-white' : 'bg-black'"
                            ></div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Camera Controls -->
              <div class="absolute bottom-4 left-4 right-4 flex gap-2">
                <button
                  @click="toggleCamera"
                  :class="[
                    'px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2',
                    cameraActive ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                  ]"
                >
                  <IconifyIcon :icon="cameraActive ? 'heroicons:video-camera-slash-20-solid' : 'heroicons:video-camera-20-solid'" class="w-4 h-4" />
                  {{ cameraActive ? 'Stop Camera' : 'Start Camera' }}
                </button>
              </div>
            </div>

            <!-- Camera Status -->
            <div class="bg-blue-50 rounded-lg p-3">
              <div class="flex items-center gap-2">
                <div class="w-2 h-2 rounded-full" :class="cameraActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'"></div>
                <span class="text-sm font-medium" :class="cameraActive ? 'text-green-700' : 'text-gray-600'">
                  {{ cameraStatus }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Manual Entry -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <IconifyIcon icon="heroicons:keyboard-20-solid" class="w-5 h-5 text-purple-600" />
            Manual Entry
          </h4>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Connection String</label>
              <textarea
                v-model="connectionString"
                placeholder="Paste the WebRTC connection string here..."
                rows="4"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              ></textarea>
              <p class="text-xs text-gray-500 mt-1">
                Connection strings start with "W:" followed by encoded data
              </p>
            </div>

            <!-- Sample Connection String -->
            <div class="bg-gray-50 rounded-lg p-3">
              <p class="text-xs font-medium text-gray-700 mb-2">Sample Connection String:</p>
              <code class="text-xs bg-white p-2 rounded block break-all">
                W:eyJ0eXBlIjoib2ZmZXIiLCJzZHAiOnsiZmluZ2VycHJpbnQiOnsiaGFzaCIOiJzaGEyNTYifX19...
              </code>
            </div>

            <button
              @click="connectManually"
              :disabled="!connectionString.trim() || isConnecting"
              class="w-full bg-purple-600 text-white py-3 px-4 rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <IconifyIcon v-if="isConnecting" icon="heroicons:arrow-path-20-solid" class="w-5 h-5 animate-spin" />
              <IconifyIcon v-else icon="heroicons:link-20-solid" class="w-5 h-5" />
              {{ isConnecting ? 'Connecting...' : 'Connect' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Connection Process Visualization -->
      <div v-if="showConnectionProcess" class="bg-white rounded-lg border border-gray-200 p-6">
        <h4 class="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IconifyIcon icon="heroicons:arrows-right-left-20-solid" class="w-5 h-5 text-green-600" />
          Connection Process
        </h4>

        <div class="space-y-4">
          <!-- Connection Steps -->
          <div class="space-y-3">
            <div
              v-for="(step, index) in connectionSteps"
              :key="index"
              class="flex items-center gap-3 p-3 rounded-lg"
              :class="{
                'bg-green-50 border border-green-200': step.status === 'completed',
                'bg-blue-50 border border-blue-200': step.status === 'active',
                'bg-gray-50 border border-gray-200': step.status === 'pending'
              }"
            >
              <div class="flex-shrink-0">
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center"
                  :class="{
                    'bg-green-600 text-white': step.status === 'completed',
                    'bg-blue-600 text-white': step.status === 'active',
                    'bg-gray-400 text-white': step.status === 'pending'
                  }"
                >
                  <IconifyIcon
                    v-if="step.status === 'completed'"
                    icon="heroicons:check-20-solid"
                    class="w-4 h-4"
                  />
                  <IconifyIcon
                    v-else-if="step.status === 'active'"
                    icon="heroicons:arrow-path-20-solid"
                    class="w-4 h-4 animate-spin"
                  />
                  <span v-else>{{ index + 1 }}</span>
                </div>
              </div>
              <div class="flex-1">
                <h5 class="font-medium" :class="{
                  'text-green-900': step.status === 'completed',
                  'text-blue-900': step.status === 'active',
                  'text-gray-700': step.status === 'pending'
                }">
                  {{ step.title }}
                </h5>
                <p class="text-sm" :class="{
                  'text-green-700': step.status === 'completed',
                  'text-blue-700': step.status === 'active',
                  'text-gray-500': step.status === 'pending'
                }">
                  {{ step.description }}
                </p>
              </div>
            </div>
          </div>

          <!-- Connection Success -->
          <div v-if="connectionSuccess" class="bg-green-50 border-l-4 border-green-400 p-4 rounded">
            <div class="flex">
              <div class="flex-shrink-0">
                <IconifyIcon icon="heroicons:check-circle-20-solid" class="w-5 h-5 text-green-400" />
              </div>
              <div class="ml-3">
                <h5 class="text-sm font-medium text-green-800">Connection Established!</h5>
                <p class="text-sm text-green-700 mt-1">
                  You're now connected to the host. You can start chatting, sharing files, or video calling!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Demo -->
      <div class="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
        <div class="flex items-start gap-3">
          <IconifyIcon icon="heroicons:sparkles-20-solid" class="w-5 h-5 text-indigo-600 mt-0.5" />
          <div>
            <h5 class="font-medium text-indigo-900 mb-1">Quick Demo</h5>
            <p class="text-sm text-indigo-800">
              Click the "Try Demo Scan" button below to simulate scanning a QR code and see the connection process in action!
            </p>
            <button
              @click="tryDemoScan"
              class="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
            >
              <IconifyIcon icon="heroicons:play-20-solid" class="w-4 h-4" />
              Try Demo Scan
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import IconifyIcon from '../IconifyIcon.vue'

// Props
const props = defineProps<{
  onConnect?: () => void
}>()

// Reactive state
const cameraActive = ref(false)
const connectionString = ref('')
const isConnecting = ref(false)
const showConnectionProcess = ref(false)
const connectionSuccess = ref(false)

// Connection steps
const connectionSteps = ref([
  {
    title: 'Decoding QR Code',
    description: 'Extracting WebRTC session information',
    status: 'pending' as 'pending' | 'active' | 'completed'
  },
  {
    title: 'Initializing WebRTC',
    description: 'Setting up peer connection',
    status: 'pending' as 'pending' | 'active' | 'completed'
  },
  {
    title: 'Exchanging Candidates',
    description: 'Negotiating connection path',
    status: 'pending' as 'pending' | 'active' | 'completed'
  },
  {
    title: 'Establishing Connection',
    description: 'Creating direct P2P link',
    status: 'pending' as 'pending' | 'active' | 'completed'
  }
])

// Computed
const cameraStatus = computed(() => {
  if (!cameraActive.value) return 'Camera off'
  return 'Scanning for QR codes...'
})

// Methods
const toggleCamera = () => {
  cameraActive.value = !cameraActive.value

  if (cameraActive.value) {
    // Simulate finding a QR code after 2 seconds
    setTimeout(() => {
      simulateQRScan()
    }, 2000)
  }
}

const simulateQRScan = () => {
  showConnectionProcess.value = true
  runConnectionProcess()
}

const connectManually = async () => {
  if (!connectionString.value.trim()) return

  isConnecting.value = true
  showConnectionProcess.value = true

  await runConnectionProcess()

  isConnecting.value = false
}

const tryDemoScan = () => {
  connectionString.value = 'W:eyJ0eXBlIjoib2ZmZXIiLCJzZHAiOnsiZmluZ2VycHJpbnQiOnsiaGFzaCI6InNoYTI1NiJ9fX0='
  connectManually()
}

const runConnectionProcess = async () => {
  for (let i = 0; i < connectionSteps.value.length; i++) {
    connectionSteps.value[i].status = 'active'

    await new Promise(resolve => setTimeout(resolve, 1000))

    connectionSteps.value[i].status = 'completed'

    if (i < connectionSteps.value.length - 1) {
      connectionSteps.value[i + 1].status = 'active'
    }
  }

  connectionSuccess.value = true

  // Auto-advance to next step
  setTimeout(() => {
    if (props.onConnect) {
      props.onConnect()
    }
  }, 2000)
}
</script>

<style scoped>
.tutorial-client {
  /* Component-specific styles */
}
</style>